/**
 * Device event log: client-side error and metrics reporting.
 *
 * Everything funnels through `report()`, which queues events and flushes them
 * in small batches to POST /api/client/events. `startTelemetry()` wires up
 * the browser-only parts (error handlers, session.start, the crash check) and
 * is called once from src/app.tsx.
 *
 * The queueing/rate-limit logic (EventQueue) and the crash marker helpers
 * (readMarker/writeMarker/clearMarker) take their storage as a parameter so
 * tests/telemetry.test.ts can exercise them without a browser.
 */

import { getPeonName } from "./store.ts";
import { onAudioEvent } from "./audio.ts";

export interface TelemetryEvent {
  kind: string;
  message?: string;
  detail?: unknown;
  at: string;
}

const ENDPOINT = "/api/client/events";
const FLUSH_QUIET_MS = 2_000;
const FLUSH_MAX_BATCH = 10;
const RATE_LIMIT_PER_MIN = 30;
const RATE_WINDOW_MS = 60_000;

// ---------- batching + rate-limit queue (pure) ----------

/** Batches events for sending and enforces the per-minute rate limit. No DOM/timer/network access. */
export class EventQueue {
  private items: TelemetryEvent[] = [];
  private windowStart: number;
  private windowCount = 0;
  private droppedNoted = false;
  private now: () => number;

  constructor(now: () => number = Date.now) {
    this.now = now;
    this.windowStart = this.now();
  }

  /**
   * Queues an event, subject to 30/minute. Events past the limit are dropped;
   * the first drop in a window queues a single `telemetry.dropped` counter
   * event so the server sees that something was lost.
   */
  push(event: TelemetryEvent): void {
    const t = this.now();
    if (t - this.windowStart >= RATE_WINDOW_MS) {
      this.windowStart = t;
      this.windowCount = 0;
      this.droppedNoted = false;
    }
    if (this.windowCount >= RATE_LIMIT_PER_MIN) {
      if (!this.droppedNoted) {
        this.droppedNoted = true;
        this.items.push({ kind: "telemetry.dropped", message: "", detail: {}, at: new Date(t).toISOString() });
      }
      return;
    }
    this.windowCount++;
    this.items.push(event);
  }

  get length(): number {
    return this.items.length;
  }

  /** True once a batch is big enough to send right away instead of waiting for the quiet timer. */
  get full(): boolean {
    return this.items.length >= FLUSH_MAX_BATCH;
  }

  /** Removes and returns everything queued so far. */
  takeAll(): TelemetryEvent[] {
    const batch = this.items;
    this.items = [];
    return batch;
  }
}

// ---------- crash marker (pure, storage injected) ----------

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface OperationMarker {
  stage: string;
  detail: unknown;
  at: string;
}

const MARKER_KEY = "op.inflight";

/** Writes the in-flight marker synchronously, before any heavy work starts. */
export function writeMarker(storage: StorageLike, stage: string, detail: unknown, now: () => number = Date.now): void {
  try {
    storage.setItem(MARKER_KEY, JSON.stringify({ stage, detail, at: new Date(now()).toISOString() }));
  } catch {
    /* private mode etc. */
  }
}

export function clearMarker(storage: StorageLike): void {
  try {
    storage.removeItem(MARKER_KEY);
  } catch {
    /* private mode etc. */
  }
}

/** Reads back a marker left by a previous page load, or null if there isn't one (or it's unreadable). */
export function readMarker(storage: StorageLike): OperationMarker | null {
  try {
    const raw = storage.getItem(MARKER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OperationMarker>;
    if (typeof parsed.stage !== "string" || typeof parsed.at !== "string") return null;
    return { stage: parsed.stage, detail: parsed.detail, at: parsed.at };
  } catch {
    return null;
  }
}

// ---------- browser wiring ----------

const queue = new EventQueue();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let pendingRetry: TelemetryEvent[] | null = null;
const seenErrors = new Set<string>();

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush(false);
  }, FLUSH_QUIET_MS);
}

async function send(events: TelemetryEvent[], beacon: boolean): Promise<boolean> {
  const payload = JSON.stringify({ name: getPeonName() ?? "unknown", build: __APP_BUILD__, events });
  try {
    if (beacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      return navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "text/plain" }));
    }
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: payload,
      keepalive: beacon,
      credentials: "same-origin",
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Sends everything queued, plus one retry of the previous failed batch. Never throws. */
async function flush(beacon: boolean): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const retry = pendingRetry;
  pendingRetry = null; // this batch gets exactly one more attempt, win or lose
  const fresh = queue.takeAll();
  const events = retry ? [...retry, ...fresh] : fresh;
  if (events.length === 0) return;
  try {
    const ok = await send(events, beacon);
    if (!ok && !retry) pendingRetry = events;
  } catch {
    /* never throws */
  }
}

/** Sends whatever is queued right now, without waiting for the quiet timer. */
export async function flushNow(): Promise<void> {
  await flush(false);
}

/** Sends whatever is queued right now via sendBeacon, for callers racing a page unload (src/perf.ts). */
export function flushForUnload(): void {
  void flush(true);
}

/**
 * Queues a device event. `at` is the device time. Flushes after 2s of quiet,
 * immediately once 10 are queued, or (via startTelemetry's listeners) when
 * the page is hidden or unloaded. Never throws; failures are swallowed.
 */
export function report(kind: string, message = "", detail: unknown = {}): void {
  try {
    queue.push({ kind, message, detail, at: new Date().toISOString() });
    if (queue.full) void flush(false);
    else scheduleFlush();
  } catch {
    /* never throws */
  }
}

/** Records `stage`/`detail` as the operation currently in flight (for crash.suspected on next launch). */
export function beginOperation(stage: string, detail: unknown = {}): void {
  writeMarker(safeSessionStorage(), stage, detail);
}

/** Clears the in-flight marker once the operation has settled. */
export function endOperation(): void {
  clearMarker(safeSessionStorage());
}

function safeSessionStorage(): StorageLike {
  try {
    return sessionStorage;
  } catch {
    return { getItem: () => null, setItem: () => {}, removeItem: () => {} };
  }
}

/**
 * Runs `fn`, then reports `kind` with `{ ...detail, ms }` on success or
 * `${kind}.failed` with `{ ...detail, ms, error }` on throw. Always rethrows.
 */
export async function measure<T>(kind: string, detail: Record<string, unknown>, fn: () => Promise<T> | T): Promise<T> {
  const start = performance.now();
  try {
    const result = await fn();
    report(kind, "", { ...detail, ms: Math.round(performance.now() - start) });
    return result;
  } catch (e) {
    report(`${kind}.failed`, "", { ...detail, ms: Math.round(performance.now() - start), error: e instanceof Error ? e.message : String(e) });
    throw e;
  }
}

function installErrorHandlers(): void {
  const onError = (message: string, stack: string) => {
    if (seenErrors.has(message)) return;
    seenErrors.add(message);
    report("error.unhandled", message, { stack: stack.slice(0, 500) });
  };
  window.addEventListener("error", (e) => {
    onError(e.message || "Unknown error", e.error instanceof Error ? (e.error.stack ?? "") : "");
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    onError(message, reason instanceof Error ? (reason.stack ?? "") : "");
  });
}

function installFlushTriggers(): void {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush(true);
  });
  window.addEventListener("pagehide", () => void flush(true));
}

const SESSION_FLAG = "op.telemetrySession";

function reportSessionStart(): void {
  const storage = safeSessionStorage();
  let already = false;
  try {
    already = storage.getItem(SESSION_FLAG) !== null;
    if (!already) storage.setItem(SESSION_FLAG, "1");
  } catch {
    /* can't dedupe without storage; report anyway */
  }
  if (already) return;
  const nav = navigator as Navigator & { deviceMemory?: number; connection?: { effectiveType?: string } };
  const navEntry = performance.getEntriesByType?.("navigation")[0] as PerformanceNavigationTiming | undefined;
  report("session.start", "", {
    standalone: matchMedia("(display-mode: standalone)").matches,
    deviceMemory: nav.deviceMemory,
    cores: navigator.hardwareConcurrency,
    screen: [screen.width, screen.height, window.devicePixelRatio],
    connection: nav.connection?.effectiveType,
    navigation: navEntry?.type,
  });
}

function checkCrashMarker(): void {
  const storage = safeSessionStorage();
  const marker = readMarker(storage);
  if (!marker) return;
  clearMarker(storage);
  const navEntry = performance.getEntriesByType?.("navigation")[0] as PerformanceNavigationTiming | undefined;
  report("crash.suspected", "", { ...marker, navigation: navEntry?.type });
}

let started = false;

/** Installs error handlers and flush triggers, reports session.start once, and checks for a crash marker left by the previous load. Call once, on app mount. */
export function startTelemetry(): void {
  if (started) return;
  started = true;
  try {
    installErrorHandlers();
    installFlushTriggers();
    checkCrashMarker();
    reportSessionStart();
    // The Device log shows when sound had to start over and how the completion line went (src/audio.ts, onAudioEvent).
    onAudioEvent((kind, message, detail) => report(kind, message, detail));
  } catch {
    /* never throws */
  }
}
